import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { 
  Home, 
  BarChart3, 
  TrendingUp, 
  Settings, 
  FileText,
  Search,
  Bell,
  User,
  ChevronDown,
  Activity,
  Menu,
  Brain,
  Package,
  LogOut,
  Calendar,
  MapPin
} from "lucide-react";
import { NavLink, useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { cn } from '@/lib/utils';
import userManager from "@/hooks/oidcConfig";

import { useQuery } from '@tanstack/react-query';
import { reportApi, Report } from '@/services/api/reports';

// 重新设计的导航结构 - 简化认知负担
const baseNavigation = [
  { title: "概览中心", url: "/", icon: Home, description: "总体业务概览" },
  { 
    title: "业务模块", 
    icon: BarChart3, 
    description: "核心业务功能",
    submenu: [
      { title: "SPU排产日历", url: "/sku-calendar", icon: Calendar },
      { title: "物流监控", url: "/logistics", icon: MapPin },
      { title: "备货预测", url: "/InventoryForecast", icon: TrendingUp },
      { title: "在售商品情况", url: "/OnSaleProducts", icon: Package },
      { title: "在售商品分类", url: "/OnSaleSpuProducts", icon: Package },
      { title: "预测MAPE情况", url: "/forecast-map-status", icon: BarChart3 }
    ]
  }
];

export function TopNavigation() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchFocused, setSearchFocused] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, logout } = useAuth();

  // 获取报表列表
  const { data: reports = [] } = useQuery<Report[]>({
    queryKey: ['reports'],
    queryFn: () => reportApi.getReports({ page_size: 50, page_no: 1, searchId:null  }),
  });
  console.log(reports);
  // 合并基础导航和动态报表
  const primaryNavigation = baseNavigation.map(item => {
    if (item.title === "业务模块" && item.submenu) {
      return {
        ...item,
        submenu: [
          ...item.submenu,
          ...reports.map((report: Report) => ({
            title: report.name,
            url: `/report/${report.search_id}`,
            icon: BarChart3
          }))
        ]
      };
    }
    return item;
  });

  const handleLogout = () => {
    const identityServerBase = import.meta.env.VITE_OIDC_SERVER_URL
    const client_id = import.meta.env.VITE_OIDC_CLIENT_ID
    const name = `oidc.user:${identityServerBase}:${client_id}`
    const token = localStorage.getItem('access_token')
    if (token) {
      Object.keys(localStorage).forEach(key => {
        // 如果键不是需要保留的键，则删除它
        if (key !== name) {
            localStorage.removeItem(key);
        }
      });
      userManager.signoutRedirect()
    }
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b border-slate-200/60 bg-white/95 backdrop-blur-md supports-[backdrop-filter]:bg-white/90 shadow-sm">
      <div className="w-full max-w-none px-4 lg:px-6">
        <div className="flex h-16 items-center justify-between">
          {/* Logo & Brand - 简化设计 */}
          <div className="flex items-center space-x-8">
            <div className="flex items-center space-x-3">
              <div className="relative flex-shrink-0">
                <Activity className="h-7 w-7 text-blue-600" />
                <div className="absolute inset-0 h-7 w-7 bg-blue-600/10 rounded-full blur-lg"></div>
              </div>
              <div className="hidden sm:block">
                <h1 className="text-lg font-bold text-slate-900">
                  智能报表中心
                </h1>
                <div className="flex items-center space-x-2 text-xs text-slate-600">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  <span>实时监控</span>
                  <Brain className="h-3 w-3 text-blue-500 ml-2" />
                  <span>AI分析</span>
                </div>
              </div>
              <div className="sm:hidden">
                <h1 className="text-base font-bold text-slate-900">报表中心</h1>
              </div>
            </div>

            {/* 主导航 - 重新设计 */}
            <nav className="hidden lg:flex items-center space-x-1">
              {primaryNavigation.map((item) => (
                item.submenu ? (
                  <DropdownMenu key={item.title}>
                    <DropdownMenuTrigger asChild>
                      <Button 
                        variant="ghost" 
                        className="flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium text-slate-700 hover:text-slate-900 hover:bg-white transition-all duration-200"
                      >
                        <item.icon className="h-4 w-4" />
                        <span>{item.title}</span>
                        <ChevronDown className="h-3 w-3" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="w-48 mt-1 bg-white">
                      <DropdownMenuLabel className="text-xs text-slate-500 uppercase tracking-wider">
                        {item.description}
                      </DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      {item.submenu.map((subItem) => (
                        <DropdownMenuItem 
                          key={subItem.title}
                          onClick={() => navigate(subItem.url)}
                          className="flex items-center space-x-2 cursor-pointer hover:bg-slate-50"
                        >
                          <subItem.icon className="h-4 w-4" />
                          <span>{subItem.title}</span>
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                ) : (
                  <NavLink
                    key={item.title}
                    to={item.url}
                    end
                    className={({ isActive }) =>
                      `flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                        isActive
                          ? "bg-blue-50 text-blue-700 border border-blue-200"
                          : "text-slate-700 hover:text-slate-900 hover:bg-slate-100/80"
                      }`
                    }
                  >
                    <item.icon className="h-4 w-4" />
                    <span>{item.title}</span>
                  </NavLink>
                )
              ))}
            </nav>

            {/* 物流地图链接 */}
            <Link
              to="/logistics"
              className={cn(
                "text-sm font-medium transition-colors hover:text-primary",
                location.pathname === '/logistics'
                  ? "text-primary"
                  : "text-muted-foreground"
              )}
            >
              <div className="flex items-center gap-1">
                <MapPin className="h-4 w-4" />
                物流监控
              </div>
            </Link>
          </div>

          {/* 右侧操作区 - 优化响应式 */}
          <div className="flex items-center space-x-3">
            {/* 搜索框 - 响应式优化 */}
            {/* <div className="relative hidden md:block">
              <div className={`flex items-center transition-all duration-300 ${
                searchFocused ? 'w-72' : 'w-56'
              }`}>
                <Search className="absolute left-3 h-4 w-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="搜索报表、数据..."
                  className="w-full pl-10 pr-4 py-2 text-sm bg-slate-50/80 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 focus:bg-white transition-all"
                  onFocus={() => setSearchFocused(true)}
                  onBlur={() => setSearchFocused(false)}
                />
              </div>
            </div> */}

            {/* 快速操作 */}
            <div className="hidden xl:flex items-center space-x-3">
              <Badge variant="outline" className="text-xs border-green-200 text-green-700 bg-green-50">
                <div className="w-2 h-2 bg-green-500 rounded-full mr-1 animate-pulse"></div>
                系统正常
              </Badge>
            </div>

            {/* 通知 */}
            {/* <Button variant="ghost" size="icon" className="relative hover:bg-slate-100">
              <Bell className="h-4 w-4 text-slate-600" />
              <span className="absolute -top-1 -right-1 h-4 w-4 bg-red-500 rounded-full text-[10px] flex items-center justify-center text-white font-medium">
                3
              </span>
            </Button> */}

            {/* 用户菜单：显示名来自 tokenObj.loginIn，无 avatar/role/department */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center space-x-2 hover:bg-slate-100">
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className="bg-gradient-to-br from-blue-500 to-purple-600 text-white text-sm font-medium">
                      {(user?.name || '用户').charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="hidden sm:block text-left">
                    <p className="text-sm font-medium text-slate-900">{user?.name || '用户'}</p>
                    {user?.email ? <p className="text-xs text-slate-500">{user.email}</p> : null}
                  </div>
                  <ChevronDown className="h-3 w-3 text-slate-500" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-medium text-slate-900">{user?.name || '用户'}</p>
                    {user?.email ? <p className="text-xs text-slate-500">{user.email}</p> : null}
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                  <LogOut className="mr-2 h-4 w-4" />
                  退出登录
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* 移动端菜单按钮 */}
            <Button 
              variant="ghost" 
              size="icon" 
              className="lg:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              <Menu className="h-5 w-5" />
            </Button>
          </div>
        </div>

        {/* 移动端导航菜单 */}
        {mobileMenuOpen && (
          <div className="lg:hidden border-t border-slate-200 bg-white">
            <div className="px-4 py-4 space-y-2">
              {primaryNavigation.map((item) => (
                <div key={item.title}>
                  {item.submenu ? (
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-slate-700">
                        <item.icon className="h-4 w-4" />
                        <span>{item.title}</span>
                      </div>
                      <div className="pl-6 space-y-1">
                        {item.submenu.map((subItem) => (
                          <button
                            key={subItem.title}
                            onClick={() => {
                              navigate(subItem.url);
                              setMobileMenuOpen(false);
                            }}
                            className="flex items-center space-x-2 w-full px-3 py-2 text-sm text-slate-600 hover:text-slate-900 hover:bg-slate-50 rounded-md"
                          >
                            <subItem.icon className="h-4 w-4" />
                            <span>{subItem.title}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <NavLink
                      to={item.url}
                      onClick={() => setMobileMenuOpen(false)}
                      className={({ isActive }) =>
                        `flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium ${
                          isActive
                            ? "bg-blue-50 text-blue-700"
                            : "text-slate-700 hover:text-slate-900 hover:bg-slate-50"
                        }`
                      }
                    >
                      <item.icon className="h-4 w-4" />
                      <span>{item.title}</span>
                    </NavLink>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </header>
  );
} 